import { createFeatureSelector, createSelector } from '@ngrx/store';
import { IEmployeesState } from './employees-state';
import { StateSlice } from '../state-slice';
import {
    getEmployeeEntities,
    getEmployeePaystubsRelationship,
    getEmployeePaystubEntities,
    getEmployeeDocumentEntities,
    getEmployeeDocumentsRelationship,
    getEmployeeBenefitEntities,
    getEmployeeBenefitsRelationship,
    getEmployeeDeductionEntities,
    getEmployeeDeductionsRelationship,
    getEmployeeWageEntities,
    getEmployeeWagesRelationship
} from '../shared/entities/entities-selectors';
import { Employee } from '@core/models/employee/employee';
import { Paystub } from '@core/models/shared/paystub/paystub';
import { Document } from '@core/models/shared/document/document';
import { Benefit } from '@core/models/employee/benefit/benefit';
import { Deduction } from '@core/models/employee/deduction/deduction';
import { DeductionType } from '@core/models/employee/deduction/deduction-type';
import { EmployeeWage } from '@core/models/employee/employee-wage';
import moment from 'moment';
import { Moment } from 'moment';

export const selectEmployeesState = createFeatureSelector<IEmployeesState>(StateSlice.EMPLOYEES);

export const getEmployeesList = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.employees
);

export const getEmployeeIds = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.employees.ids
);



export const isFetchingEmployees = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.employees.isFetching
);

export const getEmployees = createSelector(
    [getEmployeeEntities, getEmployeeIds],
    (employees: { [id: string]: Employee }, employeeIds: string[]) => {
        return employeeIds
            .map((employeeId: string) => employees[employeeId])
            .sort((a: Employee, b: Employee) => a.name.localeCompare(b.name));
    }
);

export const getEmployeePaystubs = createSelector(
    [getEmployeePaystubEntities, getEmployeePaystubsRelationship],
    (paystubEntities: { [id: string]: Paystub }, relationshipMap: { [id: string]: string[] }) => {
        // Get list of employeeIds
      const employeeIds: string[] = Object.keys(relationshipMap);
      let result = {};

      employeeIds.forEach((employeeId) => {
          const paystubIds: string[] = relationshipMap.hasOwnProperty(employeeId) && relationshipMap[employeeId] || [];

          if (paystubEntities && paystubIds.length) {
              result[employeeId] =
                paystubIds.map((paystubId) => paystubEntities.hasOwnProperty(paystubId) && paystubEntities[paystubId]);
          }
      });

      return result;
    }
);

export const getViewedEmployeeId = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.viewedEmployeeId
);

export const getViewedEmployee = createSelector(
    [getEmployeeEntities, getViewedEmployeeId],
    (employees: { [id: string]: Employee }, viewedEmployeeId: string) => {
        return viewedEmployeeId ? employees[viewedEmployeeId] : null;
    }
);

// Paystubs
export const getEmployeePaystubList = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.paystubs
);

export const getPaystubsForViewedEmployee = createSelector(
    [getViewedEmployee, getEmployeePaystubEntities, getEmployeePaystubsRelationship],
    (viewedEmployee: Employee, paystubEntities: { [id: string]: Paystub }, relationMap) => {

        // Get list of connectionIds
        let paystubs: Paystub[] = [];
        const employeeId: string = viewedEmployee && viewedEmployee.id;
        const paystubIds: string[] = relationMap.hasOwnProperty(employeeId) && relationMap[employeeId] || [];
  
        if (paystubEntities && paystubIds.length && employeeId) {
            paystubs =
              paystubIds
                .filter((paystubId: string) => paystubEntities.hasOwnProperty(paystubId) && !!paystubEntities[paystubId])
                .map((paystubId: string) => Object.assign(new Paystub(), paystubEntities[paystubId]))
        }
  
        return paystubs;
    });

export const isFetchingPaystubs = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.paystubs.isFetching
);

export const isFetchingMorePaystubs = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.paystubs.isFetchingMore
);

// Documents
export const getEmployeeDocumentList = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.documents
);

export const getDocumentsForViewedEmployee = createSelector(
    [getViewedEmployee, getEmployeeDocumentEntities, getEmployeeDocumentsRelationship],
    (viewedEmployee: Employee, documentEntities: { [id: string]: Document }, relationMap) => {

        // Get list of connectionIds
        let documents: Document[] = [];
        const employeeId: string = viewedEmployee && viewedEmployee.id;
        const documentIds: string[] = relationMap.hasOwnProperty(employeeId) && relationMap[employeeId] || [];
  
        if (documentEntities && documentIds.length && employeeId) {
            documents =
            documentIds
                .filter((documentId: string) => documentEntities.hasOwnProperty(documentId) && !!documentEntities[documentId])
                .map((documentId: string) => Object.assign(new Paystub(), documentEntities[documentId]))
        }
  
        return documents;
    });

export const isFetchingDocuments = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.documents.isFetching
);

export const isFetchingMoreDocuments = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.documents.isFetchingMore
);

// Benefits
export const getEmployeeBenefitList = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.benefits
);

export const getBenefitsForViewedEmployee = createSelector(
    [getViewedEmployee, getEmployeeBenefitEntities, getEmployeeBenefitsRelationship],
    (viewedEmployee: Employee, benefitEntities: { [id: string]: Benefit }, relationMap) => {

        // Get list of connectionIds
        let benefits: Benefit[] = [];
        const employeeId: string = viewedEmployee && viewedEmployee.id;
        const benefitIds: string[] = relationMap.hasOwnProperty(employeeId) && relationMap[employeeId] || [];
  
        if (benefitEntities && benefitIds.length && employeeId) {
            benefits =
            benefitIds
                .filter((benefitId: string) => benefitEntities.hasOwnProperty(benefitId) && !!benefitEntities[benefitId])
                .map((benefitId: string) => Object.assign(new Benefit(), benefitEntities[benefitId]))
        }
  
        return benefits;
    });

export const isFetchingBenefits = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.benefits.isFetching
);

export const isFetchingMoreBenefits = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.benefits.isFetchingMore
);


// Deductions
export const getEmployeeDeductionList = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.deductions
);

export const getDeductionsForViewedEmployee = createSelector(
    [getViewedEmployee, getEmployeeDeductionEntities, getEmployeeDeductionsRelationship],
    (viewedEmployee: Employee, deductionEntities: { [id: string]: Deduction }, relationMap) => {

        // Get list of connectionIds
        let deductions: Deduction[] = [];
        const employeeId: string = viewedEmployee && viewedEmployee.id;
        const deductionIds: string[] = relationMap.hasOwnProperty(employeeId) && relationMap[employeeId] || [];
        const today: Moment = moment();
  
        if (deductionEntities && deductionIds.length && employeeId) {
            deductions =
            deductionIds
                .filter((deductionId: string) => deductionEntities.hasOwnProperty(deductionId) && !!deductionEntities[deductionId])
                .map((deductionId: string) => Object.assign(new Deduction(), {
                    ...deductionEntities[deductionId],
                    isActive: !deductionEntities[deductionId].effectiveEnd || deductionEntities[deductionId].effectiveEnd?.isSameOrAfter(today)
                }))
        }
  
        return deductions;
    });

export const getChildSupportDeductionsForViewedEmployee = createSelector(
    getDeductionsForViewedEmployee, 
    (deductions: Deduction[]) => (deductions || []).filter((deduction: Deduction) => deduction.type === DeductionType.CHILD_SUPPORT)
);

export const getMiscellaneousDeductionsForViewedEmployee = createSelector(
    getDeductionsForViewedEmployee, 
    (deductions: Deduction[]) => (deductions || []).filter((deduction: Deduction) => deduction.type === DeductionType.MISCELLANEOUS)
);

export const getMiscellaneousGarnishmentDeductionsForViewedEmployee = createSelector(
    getDeductionsForViewedEmployee, 
    (deductions: Deduction[]) => (deductions || []).filter((deduction: Deduction) => deduction.type === DeductionType.MISCELLANEOUS_GARNISHMENT)
);

export const isFetchingDeductions = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.deductions.isFetching
);

export const isFetchingMoreDeductions = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.deductions.isFetchingMore
);

// Wages
export const getEmployeeWages = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.wages
);

export const getViewedEmployeeWages = createSelector(
    [getViewedEmployee, getEmployeeWageEntities, getEmployeeWagesRelationship],
    (viewedEmployee: Employee, wageEntities: { [id: string]: EmployeeWage }, relationshipMap: { [id: string]: string[] }) => {
        // Get list of employeeIds
        const employeeId: string = viewedEmployee && viewedEmployee.id;
        const wageIds: string[] = relationshipMap.hasOwnProperty(employeeId) && relationshipMap[employeeId] || [];

        if (wageEntities && wageIds.length && employeeId) {
            const now: Moment = moment();
            return wageIds
                .filter((wageId: string) => wageEntities.hasOwnProperty(wageId) && !!wageEntities[wageId])
                .map((wageId: string) => Object.assign(new EmployeeWage(), {
                    ...wageEntities[wageId],
                    statusLabel: wageEntities[wageId].id  === viewedEmployee.currentWage?.id
                            ? 'Employees.Wage.Status.Current'
                            : ( wageEntities[wageId].effective.isAfter(now)
                                ? 'Employees.Wage.Status.Upcoming'
                                : ''),
                    isUpcoming: wageEntities[wageId].effective.isAfter(now),
                    isActive: wageEntities[wageId].id  === viewedEmployee.currentWage?.id
                }));
        } else {
            return [];
        }
    }
);

export const isFetchingWages = createSelector(
    selectEmployeesState,
    (state: IEmployeesState) => state.wages.isFetching
);
